// src/pages/AssetDetailsPage.js

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

// ✅ lowercase filename matches disk: assetDetailsService.js
// ✅ AssetDetailsError (capital A) matches the export in the service
import {
  assetDetailsService,
  AssetDetailsError,
} from "../services/assetDetailsService";

import {
  Box, Typography, Button, Card, CardContent,
  Divider, Chip, CircularProgress, Alert, Stack, Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";

const ERROR_MESSAGES = {
  INVALID_ASSET_ID: "The asset ID in the URL is invalid.",
  ASSET_NOT_FOUND:  "This asset does not exist or has been removed.",
  FETCH_FAILED:     "Unable to load asset details. Please check your connection.",
  DEFAULT:          "An unexpected error occurred.",
};

const STATUS_COLORS = {
  available:   "success",
  assigned:    "primary",
  maintenance: "warning",
  retired:     "error",
  unknown:     "default",
};

const getStatusChipColor = (status = "") => {
  const key = status.toLowerCase().replace(/\s+/g, "-");
  return STATUS_COLORS[key] || "default";
};

const formatDate = (date) => {
  if (!date || !(date instanceof Date)) return "N/A";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const DetailRow = ({ label, children }) => (
  <Grid container sx={{ mb: 1.8 }}>
    <Grid item xs={12} sm="auto">
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          minWidth: { sm: "180px" },
          width: { xs: "100%", sm: "180px" },
          pr: 2,
          lineHeight: 1.6,
        }}
      >
        {label}:
      </Typography>
    </Grid>
    <Grid item xs={12} sm>
      <Typography
        variant="body2"
        sx={{ color: "text.primary", lineHeight: 1.6 }}
      >
        {children}
      </Typography>
    </Grid>
  </Grid>
);

const DetailSection = ({ title, children, isLast = false }) => (
  <Box sx={{ mb: isLast ? 0 : 3.5, pb: isLast ? 0 : 2.5 }}>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 700, mb: 2, color: "text.primary", fontSize: "1rem" }}
    >
      {title}
    </Typography>
    {children}
    {!isLast && <Divider sx={{ mt: 2.5, borderColor: "#eee" }} />}
  </Box>
);

const LoadingView = () => (
  <Box
    sx={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      py: 10, gap: 2,
    }}
    aria-live="polite"
    aria-busy="true"
  >
    <CircularProgress size={42} />
    <Typography variant="body2" color="text.secondary">
      Loading asset details...
    </Typography>
  </Box>
);

const ErrorView = ({ message, onBack }) => (
  <Box
    sx={{
      display: "flex", flexDirection: "column",
      alignItems: "center", py: 10, px: 2, gap: 2,
    }}
    role="alert"
  >
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon />}
      sx={{ maxWidth: 500, width: "100%" }}
    >
      {message}
    </Alert>
    <Button
      variant="outlined"
      color="inherit"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
    >
      Back to Assets
    </Button>
  </Box>
);

const AssetDetailsPage = () => {
  const { assetId } = useParams();
  const navigate    = useNavigate();

  const [asset,   setAsset]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const loadAsset = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const formattedAsset = await assetDetailsService.getAssetById(assetId);
      setAsset(formattedAsset);
    } catch (err) {
      // ✅ AssetDetailsError with capital A matches the export
      const code    = err instanceof AssetDetailsError ? err.code : "DEFAULT";
      const message = ERROR_MESSAGES[code] || ERROR_MESSAGES.DEFAULT;
      setError({ code, message });
      console.error("[AssetDetailsPage] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => { loadAsset(); }, [loadAsset]);

  useEffect(() => {
    if (!asset) return;
    const unsubscribe = assetDetailsService.subscribeToAsset(
      assetId,
      (updatedAsset) => { if (updatedAsset) setAsset(updatedAsset); },
    );
    return () => { if (typeof unsubscribe === "function") unsubscribe(); };
  }, [assetId, asset]);

  const handleBackNavigation = () => navigate("/assets");
  const handleEditNavigation = () => navigate(`/assets/${assetId}/edit`);

  if (loading) return <LoadingView />;
  if (error)   return <ErrorView message={error.message} onBack={handleBackNavigation} />;
  if (!asset)  return null;

  return (
    <Box
      sx={{
        maxWidth: "1000px",
        mx: "auto",
        mt: 2.5,
        mb: 2.5,
        px: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2.5} sx={{ mb: 3.5 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackNavigation}
          aria-label="Back to Assets list"
          sx={{
            color: "primary.main",
            fontWeight: 500,
            fontSize: "1rem",
            textTransform: "none",
            p: 0,
            minWidth: "auto",
            "&:hover": { background: "none", textDecoration: "underline" },
          }}
        >
          Back to Assets
        </Button>

        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Asset Details
        </Typography>

        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditNavigation}
          aria-label={`Edit asset ${asset.assetName}`}
          sx={{ textTransform: "none" }}
        >
          Edit
        </Button>
      </Stack>

      <Card
        elevation={3}
        sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>

          <DetailSection title="Basic Information">
            <DetailRow label="Asset ID">{asset.assetId}</DetailRow>
            <DetailRow label="Name">{asset.assetName}</DetailRow>
            <DetailRow label="Category">{asset.category}</DetailRow>
            <DetailRow label="Type">{asset.assetType}</DetailRow>
            <DetailRow label="Description">{asset.assetDescription}</DetailRow>
            <DetailRow label="QR Code No">{asset.qrCodeNo}</DetailRow>
          </DetailSection>

          <DetailSection title="Company & Location">
            <DetailRow label="Company">{asset.company}</DetailRow>
            <DetailRow label="Location">{asset.location}</DetailRow>
            <DetailRow label="Location Code">{asset.locationCode}</DetailRow>
          </DetailSection>

          <DetailSection title="Purchase Details">
            <DetailRow label="Vendor">{asset.vendor}</DetailRow>
            <DetailRow label="Cost">{formatCurrency(asset.cost)}</DetailRow>
            <DetailRow label="Invoice Number">{asset.invoiceNumber}</DetailRow>
            <DetailRow label="Warranty Until">{formatDate(asset.warranty)}</DetailRow>
          </DetailSection>

          <DetailSection title="Assignment & Status">
            <DetailRow label="Status">
              <Chip
                label={asset.status}
                color={getStatusChipColor(asset.status)}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                  borderRadius: "12px",
                }}
              />
            </DetailRow>
            <DetailRow label="Assigned To">
              {asset.assignedTo || "Unassigned"}
            </DetailRow>
          </DetailSection>

          <DetailSection title="System Information" isLast>
            <DetailRow label="Created At">{formatDate(asset.createdAt)}</DetailRow>
            <DetailRow label="Last Updated">{formatDate(asset.updatedAt)}</DetailRow>
          </DetailSection>

        </CardContent>
      </Card>
    </Box>
  );
};

export default AssetDetailsPage;